import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import type DeviceOrderedJournalEvent from "parog-web/models/device-ordered-journal-event";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type ConnectedDeviceRevokedJournalEvent from "./connected-device-revoked-journal-event";

type WithingsOrderStatusType = keyof typeof withingsOrderStatuses | undefined;

type WithingsDeliveryStatusType =
  | keyof typeof withingsDeliveryStatuses
  | undefined;

/**
 * keys are Withings exhaustive order status list
 * values map each Withings status to an internal category
 */
const withingsOrderStatuses = {
  CREATED: "ongoing",
  ADDRESS: "ongoing",
  VERIFICATION: "ongoing",
  VERIFIED: "ongoing",
  PROCESSING: "ongoing",
  OPEN: "ongoing",
  BACKHOLD: "ongoing",

  "ADDRESS ERROR": "failed",
  FAILED: "failed",
  TRASHED: "failed",

  SHIPPED: "shipped"
} as const;

/**
 * keys are Withings exhaustive delivery status list
 * values map each Withings status to an internal category
 */
const withingsDeliveryStatuses = {
  info_received: "ongoing",
  in_transit: "ongoing",
  delayed: "ongoing",
  pickup: "ongoing",

  failed_attempt: "failed",
  return: "failed",
  expired: "failed",

  pending: "pending",
  exception: "pending",

  delivered: "delivered"
} as const;

//---- Automatically generated, don't update this part ----

type DeviceProviderStringEnum = "IMEI" | "WITHINGS";

/**
 * Created once a request has been sent to the Withings API or when a device is received.
 */
export default class ConnectedDeviceAcquiredJournalEvent extends JournalEvent {
  static override type = "connected-device-acquired-journal-event";

  @attr("string") declare deviceProvider: DeviceProviderStringEnum;

  /**
   * The device_id will be, a partial IMEI or a Withings ID depending of the device-provider.
   * Actually this id is the `order_id` for Withings
   * For the IMEI we will use the 6 digits of the vendor specific serial number:
   * https://en.wikipedia.org/wiki/International_Mobile_Equipment_Identity
   */
  @attr("string") declare deviceId: string;

  /**
   * The mac adress of the device (available only once the device was shipped)
   */
  @attr("string") declare macAddress: string;

  /**
   * The model of the device (available only once the device was shipped)
   */
  @attr("string") declare deviceModel: string;

  /**
   * The tracking number to follow the dropshipment (available only once the device was shipped)
   */
  @attr("string") declare trackingNumber: string;

  @belongsTo("device-ordered-journal-event", {
    async: false,
    inverse: "connectedDeviceAcquiredJournalEvent"
  })
  declare deviceOrderedJournalEvent: DeviceOrderedJournalEvent;

  @belongsTo("connected-device-revoked-journal-event", {
    async: false,
    inverse: "connectedDeviceAcquiredJournalEvent"
  })
  declare connectedDeviceRevokedJournalEvent: ConnectedDeviceRevokedJournalEvent;

  static override validations(intl: IntlService) {
    return {
      deviceProvider: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.connected-device-acquired-journal-event.device-provider"
          )
        }),
        validateInclusion({
          list: ["IMEI", "WITHINGS"],
          description: intl.t(
            "validations.connected-device-acquired-journal-event.device-provider"
          )
        })
      ],
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.connected-device-acquired-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  /**
   * The withings status of the order
   */
  @attr("string") declare withingsOrderStatus: WithingsOrderStatusType;

  /**
   * The withings status of the delivery (available only once the device was shipped)
   */
  @attr("string") declare withingsDeliveryStatus: WithingsDeliveryStatusType;

  get orderStatus() {
    if (!this.withingsOrderStatus) return;

    return withingsOrderStatuses[this.withingsOrderStatus];
  }

  get deliveryStatus() {
    if (!this.withingsDeliveryStatus) return;

    return withingsDeliveryStatuses[this.withingsDeliveryStatus];
  }

  @belongsTo("patient", {
    async: false,
    inverse: "connectedDeviceAcquiredJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "connected-device-acquired-journal-event": ConnectedDeviceAcquiredJournalEvent;
  }
}
